import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getGameMeta } from '@shared/data/gamesMeta';
import { filterChaptersByAddons } from '@shared/data/scoringForSelection';
import { chapterIndexFromSectionId } from '@shared/state/gamePrefs';
import { preparePolishSpeechQueue } from '@shared/speech/polishSpeechText';
import { getPwaGameBundle } from '../content/gameBundle';
import { ContentBlocks } from '../components/ContentBlocks';
import {
  PolishSpeechBottomInsetProvider,
  PolishSpeechReaderPanel,
} from '../components/reading/PolishSpeechReaderPanel';
import {
  sectionIdForChapter,
  useGamePrefs,
} from '../state/GamePrefsProvider';

function ManualReaderBody() {
  const navigate = useNavigate();
  const { gameId = '', chapterIndex: chapterParam } = useParams();
  const game = getGameMeta(gameId);
  const {
    ready,
    getPrefs,
    toggleRead,
    setLastSection,
    setChecklistChecked,
    checklistMap,
    readingPercent,
  } = useGamePrefs();

  const prefs = getPrefs(gameId);
  const bundle = useMemo(() => getPwaGameBundle(gameId), [gameId]);
  const chapters = useMemo(
    () => filterChaptersByAddons(bundle.manual.chapters, prefs),
    [bundle.manual.chapters, prefs]
  );

  const chapterIndex = Math.min(
    Math.max(Number(chapterParam ?? 0) || 0, 0),
    Math.max(chapters.length - 1, 0)
  );
  const chapter = chapters[chapterIndex];
  const sectionId = sectionIdForChapter(chapterIndex);
  const isRead = prefs.readSectionIds.includes(sectionId);
  const percent = readingPercent(gameId, chapters.length);
  const checklistState = checklistMap(gameId);
  const restoredRef = useRef(false);
  const [activeFragmentId, setActiveFragmentId] = useState<string | null>(null);

  useEffect(() => {
    restoredRef.current = false;
  }, [gameId]);

  useEffect(() => {
    if (!ready || restoredRef.current) return;
    restoredRef.current = true;
    if (chapterParam != null) return;
    const savedIndex = chapterIndexFromSectionId(prefs.lastSectionId);
    if (savedIndex != null && savedIndex > 0) {
      navigate(`/game/${gameId}/manual/${savedIndex}`, { replace: true });
    }
  }, [ready, chapterParam, gameId, navigate, prefs.lastSectionId]);

  useEffect(() => {
    if (!ready || !chapter) return;
    if (prefs.lastSectionId === sectionId) return;
    setLastSection(gameId, sectionId);
  }, [ready, chapter, gameId, sectionId, prefs.lastSectionId, setLastSection]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [gameId, chapterIndex]);

  const goToChapter = (index: number) => {
    const next = Math.min(Math.max(index, 0), Math.max(chapters.length - 1, 0));
    if (next === chapterIndex) return;
    navigate(`/game/${gameId}/manual/${next}`);
  };

  if (!game || !chapter) {
    return (
      <div className="empty">
        <h1>Brak rozdziałów</h1>
        <Link to={`/game/${gameId}`}>Wróć</Link>
      </div>
    );
  }

  const continueIndex = chapterIndexFromSectionId(prefs.lastSectionId);

  return (
    <section className="manual-screen">
      <div className="progress-track" aria-hidden>
        <div className="progress-bar" style={{ width: `${percent}%` }} />
      </div>

      <div className="reader-layout">
        <aside className="toc">
          <h2>Spis treści</h2>
          <p className="meta" data-testid="reading-percent">
            Przeczytane: {percent}%
          </p>
          {continueIndex != null && continueIndex !== chapterIndex ? (
            <button
              type="button"
              className="ghost-btn"
              style={{ width: '100%', marginBottom: 10 }}
              data-testid="continue-reading"
              onClick={() => goToChapter(continueIndex)}
            >
              Kontynuuj czytanie
            </button>
          ) : null}
          <div className="toc-list">
            {chapters.map((item, index) => {
              const id = sectionIdForChapter(index);
              const read = prefs.readSectionIds.includes(id);
              return (
                <button
                  key={item.title + index}
                  type="button"
                  className={`toc-item${index === chapterIndex ? ' active' : ''}${read ? ' read' : ''}`}
                  data-testid={`toc-${index}`}
                  onClick={() => goToChapter(index)}
                >
                  {index + 1}. {item.title}
                  {read ? ' ✓' : ''}
                </button>
              );
            })}
          </div>
        </aside>

        <article className="reader-panel">
          <p className="eyebrow">{game.title}</p>
          <h1 data-testid="chapter-title">{chapter.title}</h1>
          <p className="meta" data-testid="chapter-position" style={{ marginTop: 8 }}>
            Rozdział {chapterIndex + 1} / {chapters.length}
          </p>

          <div className="reader-actions">
            <button
              type="button"
              className={`nav-btn${isRead ? ' marked' : ''}`}
              data-testid="mark-read"
              onClick={() => toggleRead(gameId, sectionId)}
            >
              {isRead ? 'Cofnij przeczytane' : 'Oznacz jako przeczytane'}
            </button>
          </div>

          <div style={{ marginTop: 18 }}>
            <ContentBlocks
              blocks={chapter.content}
              checklistId={`${gameId}:manual:${chapterIndex}`}
              checklistState={checklistState}
              onToggleChecklist={(itemKey, checked) => {
                setChecklistChecked(gameId, itemKey, checked);
              }}
              activeFragmentId={activeFragmentId}
            />
          </div>

          <div className="chapter-nav" data-testid="chapter-nav">
            <button
              type="button"
              className="nav-btn"
              disabled={chapterIndex <= 0}
              data-testid="nav-back"
              onClick={() => goToChapter(chapterIndex - 1)}
            >
              Wstecz
            </button>
            <button
              type="button"
              className="nav-btn"
              disabled={chapterIndex >= chapters.length - 1}
              data-testid="nav-next"
              onClick={() => goToChapter(chapterIndex + 1)}
            >
              Dalej
            </button>
          </div>
        </article>
      </div>

      <PolishSpeechReaderPanel
        gameId={gameId}
        sourceKey={`${gameId}:manual:${chapterIndex}`}
        buildQueue={(filters) =>
          preparePolishSpeechQueue(chapter.title, chapter.content, filters)
        }
        onActiveFragmentChange={setActiveFragmentId}
        onAdvanceChapter={
          chapterIndex < chapters.length - 1
            ? () => goToChapter(chapterIndex + 1)
            : undefined
        }
      />
    </section>
  );
}

export function ManualScreen() {
  return (
    <PolishSpeechBottomInsetProvider>
      <ManualReaderBody />
    </PolishSpeechBottomInsetProvider>
  );
}
